import type { FC } from "react";
import {
	ChevronLeftIcon,
	ChevronRightIcon,
	FirstPageIcon,
	LastPageIcon,
} from "@/components/Common/Icons/IconFactory";

export interface SearchPaginationPanelProps {
	currentPage: number;
	totalPages: number;
	pageSize: number;
	pageSizeOptions: number[];
	visibleStart: number;
	visibleEnd: number;
	totalRows: number;
	onPageChange: (newPage: number) => void;
	onPageSizeChange: (newPageSize: number) => void;
}

// Presentational pagination panel styled to match the native AG Grid paging panel
// (First/Previous/Next/Last as Google Material Symbols SVG icons — grey when disabled,
// black when active — "Page n of nn" and the row summary grouped on the left, with
// the Page Size selector docked to the far right).
// All navigation logic (handlePageChange / page-size handling) is owned by the parent
// (SearchPage) and passed in as callbacks — this component is purely presentational.
const SearchPaginationPanel: FC<SearchPaginationPanelProps> = ({
	currentPage,
	totalPages,
	pageSize,
	pageSizeOptions,
	visibleStart,
	visibleEnd,
	totalRows,
	onPageChange,
	onPageSizeChange,
}) => {
	const isFirstPage = currentPage === 0;
	const isLastPage = currentPage + 1 >= totalPages;

	return (
		<div className="ag-paging-panel ag-unselectable border-t border-gray-200 bg-white px-3 py-2 text-sm flex items-center">
			<span className="ag-paging-page-size ml-auto flex items-center gap-4">
				<label htmlFor="search-pagination-page-size" className="ag-paging-description">
					Page Size:
				</label>
				<select
					id="search-pagination-page-size"
					aria-label="Page Size"
					value={pageSize}
					onChange={(event) => onPageSizeChange(Number(event.target.value))}
					className="ag-picker-field-wrapper border border-gray-300 bg-white rounded px-3 py-3 gap-1"
				>
					{pageSizeOptions.map((option) => (
						<option key={option} value={option}>
							{option}
						</option>
					))}
				</select>
			</span>

			<span className="ag-paging-row-summary-panel ml-10 flex items-center gap-4">
				<span className="ag-paging-row-summary">
					{visibleStart} to {visibleEnd} of {totalRows}
				</span>
			</span>

			<span className="ag-paging-page-summary-panel ml-10 flex items-center gap-4">
				<span className="ag-paging-button-group flex items-center gap-4">
					<button
						type="button"
						aria-label="First"
						title="First Page"
						disabled={isFirstPage}
						onClick={() => onPageChange(0)}
						className="ag-button ag-paging-button flex items-center justify-center"
					>
						<FirstPageIcon className={`text-lg ${isFirstPage ? "text-gray-400" : "text-black"}`} />
					</button>
					<button
						type="button"
						aria-label="Previous"
						title="Previous Page"
						disabled={isFirstPage}
						onClick={() => onPageChange(currentPage - 1)}
						className="ag-button ag-paging-button flex items-center justify-center"
					>
						<ChevronLeftIcon
							className={`text-lg ${isFirstPage ? "text-gray-400" : "text-black"}`}
						/>
					</button>

					<span className="ag-paging-description">
						Page {currentPage + 1} of {totalPages}
					</span>

					<button
						type="button"
						aria-label="Next"
						title="Next Page"
						disabled={isLastPage}
						onClick={() => onPageChange(currentPage + 1)}
						className="ag-button ag-paging-button flex items-center justify-center"
					>
						<ChevronRightIcon
							className={`text-lg ${isLastPage ? "text-gray-400" : "text-black"}`}
						/>
					</button>
					<button
						type="button"
						aria-label="Last"
						title="Last Page"
						disabled={isLastPage}
						onClick={() => onPageChange(totalPages - 1)}
						className="ag-button ag-paging-button flex items-center justify-center"
					>
						<LastPageIcon className={`text-lg ${isLastPage ? "text-gray-400" : "text-black"}`} />
					</button>
				</span>
			</span>
		</div>
	);
};

export default SearchPaginationPanel;
